import React, { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { ChatMessagePair } from './ChatMessagePair';
import { EmptyChat } from './EmptyChat';
import { FormattedChatMessage } from '../../../hooks/queries/chatTypes';
import { useViewport } from '../../../contexts/ViewportContext';
import { useStatusBarInfo } from '../../../hooks/useStatusBarInfo';

interface ChatMosaicViewProps {
  messages: FormattedChatMessage[];
  isLoading: boolean;
  isTyping: boolean;
  selectedMessageId?: string;
  onMessageSelect: (message: FormattedChatMessage) => void;
}

export function ChatMosaicView({
  messages,
  isLoading,
  isTyping,
  selectedMessageId,
  onMessageSelect,
}: ChatMosaicViewProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useViewport();
  const { isNativeApp, navigationBarHeight } = useStatusBarInfo();

  // Group messages into pairs (user question followed by AI response)
  const messagePairs = React.useMemo(() => {
    const pairs = [];
    let i = 0;

    while (i < messages.length) {
      const userMessage = messages[i];
      // Skip non-user messages at the start of a pair
      if (userMessage.role !== 'user') {
        i++;
        continue;
      }

      // Look for a corresponding assistant message
      const assistantMessage =
        i + 1 < messages.length && messages[i + 1].role === 'assistant'
          ? messages[i + 1]
          : undefined;

      // Check if we have a typing indicator message (from retry)
      const hasTypingIndicator = assistantMessage?.metadata?.isTyping === true;

      // Add the pair
      pairs.push({
        userMessage,
        assistantMessage: hasTypingIndicator ? undefined : assistantMessage,
        // Set typing indicator if we have a typing message or no assistant message yet and UI is typing
        isTypingIndicator: hasTypingIndicator || (!assistantMessage && isTyping),
      });

      // Move to the next potential pair
      i += assistantMessage ? 2 : 1;
    }

    return pairs;
  }, [messages, isTyping]);

  // Scroll to bottom when new messages arrive or when typing state changes
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, isTyping]);

  // Only show loader on initial data load - never during tab switches or when data exists
  const shouldShowLoader =
    isLoading && messages.length === 0 && window.showLoadingIndicators !== false;

  if (shouldShowLoader) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center">
        <Loader2 className="h-10 w-10 text-blue-500 animate-spin mb-4" />
      </div>
    );
  }

  if (messages.length === 0) {
    return <EmptyChat />;
  }

  return (
    <div className="h-full overflow-y-auto">
      <div
        className="w-full py-6 px-2 box-border"
        style={{
          paddingBottom:
            isMobile && isNativeApp ? `calc(1.5rem + ${navigationBarHeight}px)` : '1.5rem',
        }}
      >
        <div className="space-y-4">
          {messagePairs.map(({ userMessage, assistantMessage, isTypingIndicator }) => (
            <ChatMessagePair
              key={userMessage.id}
              userMessage={userMessage}
              assistantMessage={assistantMessage}
              onMessageSelect={onMessageSelect}
              selectedMessageId={selectedMessageId}
              isTyping={isTypingIndicator}
            />
          ))}

          {/* Element for scrolling to bottom */}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
}